//-- Copyright (c) 2021 TMT Insights Group, LLC
import { AllTableColumnsConfig } from '@/__configs/config_types';
import { getLineageMetaTitle } from '@/_utils/utils';
import { Order, OrderDetail, OrderJob } from '@/redux/slices/slice_types';
import { Box, Grid, Typography } from '@mui/material';
import {
  AssigneeSelect,
  DateTimeDisplay,
  EditableTextCell,
  OrdersOverflowMenu,
  OrdersProgressBar,
  OrdersRowExpand,
  PolarisIcon,
  TableStatus
} from '../../../../exports/columnCommonComponents';

const columns: AllTableColumnsConfig<OrderDetail> = {
  expandable: {
    label: '',
    isExpandable: true,
    disableExpand: (row: OrderDetail) => row.status === 'draft' || row.order_type === 'bulk',
    render: (props: { item: Order; isCollapsed: boolean }) => {
      const extraColumns = [
        {
          label: 'Title',
          width: 300,
          render: (job: OrderJob) => (
            <>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {job.title.lineage_meta?.series_name}
              </Typography>
              <Typography variant="caption">{getLineageMetaTitle(job.title)}</Typography>
              {job.version?.name && (
                <>
                  <br />
                  <Typography variant="caption" sx={{ color: '#667799' }}>
                    {job.version?.name}
                  </Typography>
                </>
              )}
            </>
          )
        },
        {
          label: 'External ID',
          minWidth: 70,
          render: (job: OrderJob) => job.version?.external_id || '-'
        }
      ];
      return <OrdersRowExpand extraColumns={extraColumns} {...props} />;
    }
  },
  hid: {
    label: 'Order ID',
    clickCopy: true,
    sortField: 'id',
    render: (cell: OrderDetail['hid']) => <strong>{cell || '-'}</strong>
  },
  description: {
    label: 'Description',
    clickCopy: true,
    render: (cell: OrderDetail['description'], row: OrderDetail) => {
      return (
        <Grid container alignItems="center" flexWrap="nowrap">
          {row.order_type === 'bulk' && (
            <Box sx={{ color: 'secondary.main', mr: 1, height: 20, width: 20, float: 'left' }}>
              <PolarisIcon icon="bulk" height={20} />
            </Box>
          )}
          <EditableTextCell value={cell} row={row} update="order" field="description" emptyText="-" />
        </Grid>
      );
    },
    minWidth: 210,
    required: true
  },
  po_number: {
    label: 'Order Number',
    minWidth: 210,
    render: (cell: OrderDetail['po_number'], row: OrderDetail) => (
      <EditableTextCell required={false} value={cell} row={row} update="order" label="Order Number" field="po_number" emptyText="-" />
    )
  },
  managed_by_user: {
    label: 'Managed by',
    minWidth: 220,
    disableSort: true,
    render: (cell: OrderDetail['managed_by_user'], row: OrderDetail) => (
      <AssigneeSelect value={cell} id={row.id} row={row} update="order" assigns="user" emptyText="-" />
    )
  },
  billable_customer: {
    label: 'Client Name'
  },
  created_at: {
    label: 'Created',
    clickCopy: true,
    minWidth: 100,
    render: (cell: OrderDetail['created_at']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  status: {
    label: 'Status',
    render: (cell: OrderDetail['status']) => <TableStatus status={cell} />
  },
  jobs: {
    label: 'Progress',
    minWidth: 100,
    disableSort: true,
    render: (cell: OrderDetail['jobs'], row: Order) => <OrdersProgressBar jobs={cell} order={row} />
  },
  overflow: {
    label: '',
    isOverflow: true,
    width: 50,
    render: (_cell: any, row: Order) => <OrdersOverflowMenu order={row} />
  }
};

export default columns;
